import type { Session } from '@supabase/supabase-js'
import type { AuthMe, LiquidityMe, Me, Organization, Project } from './useAuth'
import { useDevLogger } from '#core/composables/useDevLogger'
import type { UsersMeData } from '#me'
import { $me } from '#me'
import { useQuery } from '@pinia/colada'
import { useStorage } from '@vueuse/core'

type OrganizationWithProjects = Organization & { projects: Project[] }

/**
 * The current app's me-endpoint payload: token-member (`Me`) fields are present when the app's
 * `meEndpoint` is `/users/me` and absent for the liquidity market-maker payload.
 */
type CurrentUserMe = UsersMeData & Partial<Me>

export function isUserTokenMember(
  me: AuthMe | null,
): me is Me {
  if (!me) {
    return false
  }
  return 'organizations' in me
}

function isUserMarketMaker(
  me: AuthMe | null,
): me is LiquidityMe {
  if (!me) {
    return false
  }
  return 'marketMakerId' in me
}

/**
 * This composable should only be used by the auth layer.
 * Ideally it should be a store but the migration process would be too complex, so we're re-exporting all
 * its computed properties via useAuth store.
 *
 * ATTENTION: Use useAuth to have access to all the computed properties and methods.
 */
export function useCurrentUser() {
  const logger = useDevLogger('useCurrentUser')
  const me = ref<CurrentUserMe | null>(null)
  const liquidityMe = ref<LiquidityMe | null>(null)
  const organization = ref<OrganizationWithProjects | null>(null)
  // Use undefined for storage on server-side to make it SSR-safe
  // On client, it will default to localStorage
  const selectedProjectId = useStorage<string | null>('forgd:auth:projectId', null, import.meta.server ? undefined : localStorage, {
    listenToStorageChanges: false,
  })

  const advisorEngagementQuery = useQuery({
    ...buildApiQuery('/advisor/engagements/{projectId}', {
      params: () => ({
        path: {
          projectId: String(selectedProjectId.value),
        },
      }),
    }),
    enabled: false,
  })

  // Store for advisor-selected projects that aren't in user's organizations
  const advisorProjects = ref<Map<string, Project>>(new Map())

  // Helper function to find project and its organization
  function findProjectAndOrg(projectId: string | null) {
    if (!projectId) {
      logger.debug('[findProjectAndOrg] no projectId')
      return { project: null, organization: null }
    }

    // First, search in user's organizations
    for (const org of organizations.value || []) {
      const foundProject = org.projects.find(p => p.id === projectId)
      if (foundProject) {
        logger.debug('[findProjectAndOrg] found project in user orgs', { id: foundProject.id, name: foundProject.name })
        return { project: foundProject, organization: org }
      }
    }

    // If not found in user orgs, check advisor projects
    const advisorProject = advisorProjects.value.get(projectId)
    if (advisorProject) {
      logger.debug('[findProjectAndOrg] found project in advisor projects', { id: advisorProject.id, name: advisorProject.name })
      return { project: advisorProject, organization: null }
    }

    logger.debug('[findProjectAndOrg] project not found')
    return { project: null, organization: null }
  }

  // Computed property that manages both project and selectedProjectId
  const project = computed({
    get: () => {
      return findProjectAndOrg(selectedProjectId.value).project
    },
    set: (newProject: Project | null) => {
      selectedProjectId.value = newProject?.id || null
    },
  })

  const isTokenMember = computed(() => isUserTokenMember(me.value))
  const isMarketMaker = computed(() => isUserMarketMaker(me.value))
  const isAdvisor = computed(() => {
    return isTokenMember.value && organizations.value?.some(o => o.type === 'advisor' && o.memberStatus === 'active')
  })
  const isOrganizationOwner = computed(() => organization.value?.ownerUserId === me.value?.id)
  const ticker = computed(() => project.value?.ticker?.toUpperCase())
  const isLoaded = computed(() => !!me.value || !!liquidityMe.value)
  const organizations = computed<OrganizationWithProjects[] | null>(() => me.value?.organizations ?? null)

  function clear() {
    logger.debug('[clear] clearing auth state')
    clearUserData()
    clearSelectedProject()
    // Clear advisor projects
    advisorProjects.value.clear()
  }

  function clearUserData() {
    me.value = null
    liquidityMe.value = null
    organization.value = null
  }

  function clearSelectedProject() {
    selectedProjectId.value = null
    organization.value = null
  }

  function setSelectedProject(newProject: Project) {
    logger.debug('[setSelectedProject]', newProject.name)

    // If this is an advisor project not in user's orgs, register it
    const { project: existingProject } = findProjectAndOrg(newProject.id)
    if (!existingProject) {
      registerAdvisorProject(newProject)
    }

    selectedProjectId.value = newProject.id
    const { organization: selectedProjectOrg } = findProjectAndOrg(selectedProjectId.value)
    organization.value = selectedProjectOrg
  }

  // Method to register advisor projects
  function registerAdvisorProject(projectToRegister: Project) {
    logger.debug('[registerAdvisorProject]', { id: projectToRegister.id, name: projectToRegister.name })
    advisorProjects.value.set(projectToRegister.id, projectToRegister)
  }

  /**
   * NOTE: Advisors can have an open engagement that isn’t in their assigned organizations.
   * After a page reload, selectedProjectId may point to such a project, so findProjectAndOrg()
   * returns null. In that case (advisors only), rehydrate the last open engagement and add it
   * to the registered list.
   */
  async function restoreAdvisorProjectIfMissing() {
    const { project } = findProjectAndOrg(selectedProjectId.value)
    if (project) {
      return
    }
    const { data } = await advisorEngagementQuery.refetch()
    if (!data) {
      return
    }
    registerAdvisorProject(data)
  }

  function loadOrgsAndProjects(mePayload: AuthMe | null) {
    if (!mePayload) {
      return
    }

    // Liquidity flow
    if (isUserMarketMaker(mePayload)) {
      liquidityMe.value = mePayload
      return
    }

    // if the user is a project owner, default to that project
    const activeOrganizations = organizations.value?.filter(org => org.memberStatus === 'active' && org.projects.length > 0)
    const ownedActiveOrganization = activeOrganizations?.find(org => org.ownerUserId === me.value?.id)

    // Extract project ID from current route
    const route = useRoute()
    const urlProjectId = Array.isArray(route.params.projectId) ? route.params.projectId[0] : route.params.projectId

    // First priority: URL project ID (if user has access)
    if (urlProjectId) {
      const { project: urlProject, organization: urlProjectOrg } = findProjectAndOrg(urlProjectId)
      if (urlProject) {
        // URL project ID is valid and user has access
        // Only update values if they're different
        if (selectedProjectId.value !== urlProjectId) {
          selectedProjectId.value = urlProjectId
        }
        if (organization.value !== urlProjectOrg) {
          organization.value = urlProjectOrg
        }
        logger.debug('[loadOrgsAndProjects] selected project from URL', { urlProjectId })
        return
      }
      else {
        // URL project ID is invalid or user doesn't have access
        // Log this but continue with existing logic
        logger.warn('[loadOrgsAndProjects] URL contains project ID user cannot access', { urlProjectId })
      }
    }

    // Second priority: cached selectedProjectId
    if (selectedProjectId.value) {
      const { project: selectedProject, organization: selectedProjectOrg } = findProjectAndOrg(selectedProjectId.value)
      if (selectedProject) {
        organization.value = selectedProjectOrg
        logger.debug('[loadOrgsAndProjects] found selectedProject in different org', { project: project.value, organization: organization.value })
        return
      }
    }

    // Third priority: infer selected project from user's active organizations
    organization.value = ownedActiveOrganization || activeOrganizations?.[0] || organizations.value?.[0] || null

    const inferredProject = inferSelectedProject(mePayload)
    if (inferredProject) {
      project.value = inferredProject
    }
    const selectedProjectOrg = organizations.value?.find(p => p.id === project.value?.organizationId)
    if (selectedProjectOrg) {
      organization.value = selectedProjectOrg
    }
    logger.debug('[loadOrgsAndProjects] inferred selected project', { project: project.value, selectedProjectId: selectedProjectId.value, organization: organization.value })
  }

  async function fetch(session: Session) {
    logger.debug('[fetchCurrentUser] calling $me')
    const mePayload = await $me({ headers: { Authorization: `Bearer ${session.access_token}` } })
    if (!mePayload) {
      logger.debug('[fetchCurrentUser] user data not loaded, returning false')
      return false
    }

    me.value = mePayload

    if (isAdvisor.value && selectedProjectId.value) {
      await restoreAdvisorProjectIfMissing()
    }

    loadOrgsAndProjects(mePayload)

    logger.debug('[fetchCurrentUser] user data loaded', mePayload)
    return true
  }

  return {
    me,
    liquidityMe,
    isTokenMember,
    isMarketMaker,
    isAdvisor,
    isOrganizationOwner,
    organizations,
    organization,
    project,
    ticker,
    isLoaded,
    selectedProjectId,

    fetch,
    clear,
    setSelectedProject,
    clearSelectedProject,
    registerAdvisorProject,
    findProjectAndOrg,
  }
}
